import { useState, useEffect, useMemo, useRef } from 'react'
import useWebSocket, { ReadyState } from 'react-use-websocket'
import { useYTDPerformance } from 'utils/financialCalculations'

interface StockPriceUpdate {
	ticker: string
	close: string
	start_timestamp: string
}

interface UseStockWebSocketProps {
	ticker: string
	ytdPriceOpen: string | number
	initialPrice: string | number
	onPriceUpdate?: (price: string) => void
	keepAlive?: boolean
}

// Subscription manager to track active subscriptions
const subscriptionManager = new Map<string, Set<string>>()

// Function to get all currently subscribed tickers (without duplicates)
export const getSubscribedTickers = () => {
	return Array.from(subscriptionManager.keys())
}

// Function to get subscription count for a ticker
export const getSubscriptionCount = (ticker: string) => {
	return subscriptionManager.get(ticker)?.size || 0
}

// Function to add a subscription
const addSubscription = (ticker: string, componentId: string) => {
	if (!subscriptionManager.has(ticker)) {
		subscriptionManager.set(ticker, new Set())
	}
	subscriptionManager.get(ticker)?.add(componentId)
}

// Function to remove a subscription
const removeSubscription = (ticker: string, componentId: string) => {
	const subscribers = subscriptionManager.get(ticker)
	if (subscribers) {
		subscribers.delete(componentId)
		if (subscribers.size === 0) {
			subscriptionManager.delete(ticker)
		}
	}
}

const apiKey = process.env.REACT_APP_WEBSOCKET_API_KEY
const baseUrl = process.env.REACT_APP_WEBSOCKET_URL
const socketUrl = baseUrl && apiKey ? `${baseUrl}?api_key=${apiKey}` : null

export const useStockWebSocket = ({
	ticker,
	ytdPriceOpen,
	initialPrice,
	onPriceUpdate,
	keepAlive = false
}: UseStockWebSocketProps) => {
	const [hasLivePrice, setHasLivePrice] = useState(false)
	const componentId = useMemo(() => Math.random().toString(36).substring(7), [])
	const priceMapRef = useRef<{ [ticker: string]: string }>({})

	const { sendMessage, lastMessage, readyState } = useWebSocket(socketUrl || '', {
		shouldReconnect: closeEvent => true,
		reconnectAttempts: 5,
		reconnectInterval: 3000,
		share: true, // Share the WebSocket connection across components
		retryOnError: true
	})

	// Subscribe to ticker updates when component mounts or ticker changes
	useEffect(() => {
		if (readyState === ReadyState.OPEN && ticker) {
			addSubscription(ticker, componentId)
			const subscribedTickers = getSubscribedTickers()
			if (subscribedTickers.length > 0) {
				sendMessage(
					JSON.stringify({
						action: 'subscribe',
						streams: subscribedTickers
					})
				)
			}
		}
	}, [readyState, ticker, sendMessage, componentId])

	// Cleanup subscription when component unmounts or ticker changes
	useEffect(() => {
		return () => {
			if (readyState === ReadyState.OPEN && ticker) {
				removeSubscription(ticker, componentId)
				const remainingTickers = getSubscribedTickers()
				sendMessage(
					JSON.stringify({
						action: 'unsubscribe',
						streams: [ticker]
					})
				)
				if (remainingTickers.length > 0) {
					sendMessage(
						JSON.stringify({
							action: 'subscribe',
							streams: remainingTickers
						})
					)
				}
			}
		}
	}, [readyState, ticker, sendMessage, componentId])

	// Process incoming messages and update price map
	useEffect(() => {
		if (lastMessage) {
			try {
				const data: StockPriceUpdate = JSON.parse(lastMessage.data)
				if (data.ticker) {
					priceMapRef.current[data.ticker] = data.close
					if (data.ticker === ticker) {
						setHasLivePrice(true)
						onPriceUpdate?.(data.close)
					}
				}
			} catch (error) {
				console.error('Error parsing WebSocket message:', error)
			}
		}
	}, [lastMessage, ticker, onPriceUpdate])

	// Reset hasLivePrice if ticker changes
	useEffect(() => {
		setHasLivePrice(false)
	}, [ticker])

	// Get the latest price for the current ticker
	const currentPrice = useMemo(() => {
		return priceMapRef.current[ticker] ?? initialPrice.toString()
	}, [ticker, initialPrice, priceMapRef.current[ticker]])

	const ytdPerformance = useYTDPerformance(ytdPriceOpen, currentPrice)

	return {
		currentPrice,
		ytdPerformance,
		connectionStatus: readyState,
		isConnected: readyState === ReadyState.OPEN,
		hasLivePrice
	}
}
